<template>
  <div>
    <prom-report
      v-if="report"
      :prom="prom"
      :prom-report="report"
      :previous-prom-report="previousReport"
      @set-unsaved="report => setUnsavedReport(report)"
      @save="report => postSave(report)"
      ref="promreport"
    />

    <!-- Study task info modal -->
    <modal-study-task-vue
      v-if="$activeStudyTask"
      :show="$showStudyTaskModal && $activeStudyTask?.hasModalContent"
      :study-task="$activeStudyTask"
      @closed="$setShowStudyTaskModal(false)"
    ></modal-study-task-vue>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { CURRENT_USER_ACTIONS } from '@/store/modules/currentuser';
import { PROM_ACTIONS, PROM_GETTERS } from '@/store/modules/proms';
import {
  PROMREPORT_ACTIONS,
  PROMREPORT_GETTERS
} from '@/store/modules/promreports';
import {
  STUDY_TASK_ACTIONS,
  STUDY_TASK_GETTERS,
  STUDY_TASK_MUTATIONS
} from '@/store/modules/studytasks';
import { STATUSREPORT_ACTIONS } from '@/store/modules/statusreports';
import PromReport from '@views/PromReport/components/PromReport.vue';
import PromReportModel from '@/Models/PromReportModel';
import { randomString } from '@/Helpers/strings';
import store from '@/store';
import ModalStudyTaskVue from '@views/components/Modals/ModalStudyTask.vue';

export default {
  name: 'promreporteditview',
  props: ['promid', 'promreportid'],
  components: {
    ModalStudyTaskVue,
    PromReport
  },
  data() {
    return {
      prom: null,
      report: null,
      previousReport: null,
      goingToNextStudyTask: false,
      showDiatestHelpScreeningMessage: false
    };
  },
  beforeRouteLeave(to, from, next) {
    // Refs hack to trigger modal in promreport component...
    if (
      this.$refs.promreport.savable === true &&
      this.$refs.promreport.confirmCancel === false &&
      this.$refs.promreport.confirmedCancel === false
    ) {
      this.$refs.promreport.confirmCancel = true;
      this.$refs.promreport.redirectOnCancel = to;
      next(false);
    } else {
      this.$removeUnsavedReport();

      if (!this.goingToNextStudyTask) {
        this.$clearActiveStudyTask();
      }
      next();
    }
  },
  watch: {
    async promid(promid) {
      if (promid) {
        await this.loadProm();
        await this.loadPromReport();
      }
    }
  },
  async mounted() {
    await this.loadProm();
    await this.loadPromReport();
  },
  beforeRouteEnter(to, from, next) {
    /*
    Remove unsaved report unless we are coming from login-loading
    which should mean user has refreshed or is coming straight from login.

    Direct store call done instead of using mapActions since we don't have access
    to this.* in befoureRouteEnter method.
    */
    if (from.name != 'login-loading') {
      store.dispatch(`promreports/${PROMREPORT_ACTIONS.REMOVE_UNSAVED_REPORT}`);
    }

    next();
  },
  methods: {
    ...mapActions('currentuser', {
      $getMyGroups: CURRENT_USER_ACTIONS.GET_MY_GROUPS
    }),
    ...mapActions('promreports', {
      $getUnsavedReport: PROMREPORT_ACTIONS.GET_UNSAVED_REPORT,
      $setUnsavedReport: PROMREPORT_ACTIONS.SET_UNSAVED_REPORT,
      $removeUnsavedReport: PROMREPORT_ACTIONS.REMOVE_UNSAVED_REPORT,
      $getMyPromReport: PROMREPORT_ACTIONS.GET_MY_PROMREPORT,
      $getMyLatestPromReportByProm:
        PROMREPORT_ACTIONS.GET_MY_LATEST_PROMREPORT_BY_PROM
    }),
    ...mapActions('proms', {
      $getProm: PROM_ACTIONS.GET_PROM
    }),
    ...mapActions('statusreports', {
      $getPromSuggestions: STATUSREPORT_ACTIONS.GET_PROM_SUGGESTIONS
    }),
    ...mapActions('studytasks', {
      $clearActiveStudyTask: STUDY_TASK_ACTIONS.CLEAR_ACTIVE_STUDY_TASK,
      $getMyNextStudyTask: STUDY_TASK_ACTIONS.GET_MY_NEXT_STUDY_TASK,
      $getActiveStudyTask: STUDY_TASK_ACTIONS.GET_ACTIVE_STUDY_TASK
    }),
    ...mapMutations('studytasks', {
      $setShowStudyTaskModal: STUDY_TASK_MUTATIONS.SET_SHOW_STUDY_TASK_MODAL
    }),
    async loadProm() {
      if (this.promid !== null) {
        this.prom = this.$findProm(this.promid);

        if (!this.prom || this.prom.shouldGetFullProm) {
          await this.$getProm(this.promid).then(prom => {
            this.prom = prom;
          });
        }
      }
    },
    async loadPromReport() {
      if (!this.promreportid) {
        this.$getUnsavedReport();

        if (this.prom.PrefillPreviousAnswers) {
          await this.getPreviousReport();
        }

        if (this.$unsavedReport) {
          this.report = this.$unsavedReport;
        } else {
          this.report = new PromReportModel({
            Id: randomString(),
            PromId: this.promid,
            LanguageId: this.prom.Language,
            QuestionCount: this.prom.QuestionCount
          });
        }

        this.$getActiveStudyTask(); // In case user has reloaded the page.

        if (this.$activeStudyTask) {
          this.$setShowStudyTaskModal(true);
        }
      } else {
        await this.$getMyPromReport(this.promreportid).then(report => {
          this.report = report;
        });
      }
    },
    async getPreviousReport() {
      await this.$getMyLatestPromReportByProm(this.promid).then(
        previousReport => {
          this.previousReport = previousReport;
        }
      );
    },
    setUnsavedReport(report) {
      this.$setUnsavedReport(report.__copy());
    },
    async postSave(report) {
      this.setShowDiatestHelpScreeningModal(report);
      this.$removeUnsavedReport(); // Needed in case we are redirected to same view but different PROM.
      this.$getMyGroups(); // In case user has been auto-added to group after promreport submission

      var studyTask,
        redirect = null;

      if (this.$activeStudyTask) {
        studyTask = await this.$getMyNextStudyTask();
      }

      if (studyTask) {
        redirect = studyTask.actionRoute;
        this.goingToNextStudyTask = true;
      } else if (this.showDiatestHelpScreeningMessage) {
        redirect = {
          name: 'start',
          query: { diatesthelpscreening: 1 }
        };
      } else {
        redirect = {
          name: 'promReportFollowupView',
          params: { promreportid: report.Id }
        };
        this.$getPromSuggestions();
      }

      this.$router.replace(redirect);
    },
    setShowDiatestHelpScreeningModal(report) {
      if (report.PromId != 'DIATEST-HELP_SCREENINGFRAGOR') {
        return;
      }

      const diabetesQuestion = report.Answers.find(
        aq => aq.QuestionId === 'Q1-'
      );
      const hadDiabetesForTwoYears =
        diabetesQuestion.AnswerIds.includes('Q1A1-');

      const shoppingQuestion = report.Answers.find(
        aq => aq.QuestionId === 'Q2-'
      );
      const shoppingAnswers = ['Q2A1-', 'Q2A2-', 'Q2A3-', 'Q2A4-'];
      const shopsFrequently = shoppingAnswers.some(answerId =>
        shoppingQuestion.AnswerIds.includes(answerId)
      );

      this.showDiatestHelpScreeningMessage =
        !hadDiabetesForTwoYears || !shopsFrequently;
    }
  },
  computed: {
    ...mapGetters('proms', {
      $findProm: PROM_GETTERS.FIND_PROM
    }),
    ...mapGetters('promreports', {
      $findPromReport: PROMREPORT_GETTERS.FIND_PROMREPORT,
      $unsavedReport: PROMREPORT_GETTERS.UNSAVED_REPORT
    }),
    ...mapGetters('studytasks', {
      $activeStudyTask: STUDY_TASK_GETTERS.ACTIVE_STUDY_TASK,
      $showStudyTaskModal: STUDY_TASK_GETTERS.SHOW_STUDY_TASK_MODAL
    })
  }
};
</script>

<style lang="scss" scoped></style>
